import { useEffect } from 'react';

import { getNormalizedPathname } from '@/utils/locale';

import { useRouter } from '@/hooks/useRouter';
import {
  getGlobalECookieDomain,
  isGlobalECheckoutPath,
  removeInjectedGlobalEScripts,
  removeInvalidGlobalECartIdCookies,
} from '@/lib/globalECartId';
import { buildGlobalEDataCookie } from '@/lib/globalEData';
import { logGlobalEDebug } from '@/lib/globalEDebug';
import { getRegionFromLocale } from '@/lib/regions';
import { StorageHelper } from '@/lib/storage';
import { useGlobalEContext } from '@/providers/GlobalEProvider/GlobalEProviderContext';

/**
 * Sets GlobalE cookies client-side for international shipping integration.
 *
 * CLIENT-SIDE COOKIE SETTING - Primary mechanism for cacheable pages
 * ─────────────────────────────────────────────────────────────────────────────
 * The CDN (Akamai/Cloudflare) strips Set-Cookie headers from cached responses.
 * This client-side cookie setting is the PRIMARY way users get GlobalE cookies
 * when visiting cacheable pages (homepage, PLP, PDP, CMS pages).
 *
 * Server-side middleware (SetGlobalEDataCookieMiddleware) also sets this cookie,
 * but only works on non-cacheable pages (checkout, my-account, etc.).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Global-E merchant JS is only loaded on /store/global-e/checkout/ by
 * GlobalEView. Browse pages keep GlobalE_Data cookies but never load the script,
 * which prevents ge_ApiCall requests to /undefined/ without a checkout session.
 */
export const useGlobalECookies = () => {
  const { locale, pathname } = useRouter();
  const { setLoadScript } = useGlobalEContext();

  useEffect(() => {
    if (!locale) return;

    const storageHelper = new StorageHelper();
    const domain = getGlobalECookieDomain();
    const { country, locale: newLocale } = getRegionFromLocale(locale);

    storageHelper.setCookie(
      'GlobalE_Data',
      JSON.stringify(buildGlobalEDataCookie(country.code, country.currency)),
      { domain },
    );
    storageHelper.setCookie('GlobalE_Locale', `${newLocale.locale}`, {
      domain,
    });

    removeInvalidGlobalECartIdCookies(storageHelper);

    const isGlobalECheckoutPage = isGlobalECheckoutPath(
      getNormalizedPathname(pathname),
    );

    if (isGlobalECheckoutPage) {
      logGlobalEDebug('cookies:checkout-page');
      return;
    }

    logGlobalEDebug('cookies:browse-page-unload-script');
    removeInjectedGlobalEScripts();
    setLoadScript(false);
  }, [locale, pathname, setLoadScript]);
};
